import { Head, useForm } from "@inertiajs/react"
import { Pencil } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface OrgRow {
  id: number
  name: string
  slug: string
  partner: boolean
  parent: { id: number; name: string } | null
  primary_products: string[]
  member_count: number
  created_at: string
}
interface ParentOption {
  id: number
  name: string
}
interface Props {
  organizations: OrgRow[]
  parent_options: ParentOption[]
  product_options: string[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin Console", href: "/admin/console" },
  { title: "Organizations", href: "/admin/organizations" },
]

const NO_PARENT = "none"

export default function AdminOrganizations({
  organizations,
  parent_options,
  product_options,
}: Props) {
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<OrgRow | null>(null)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Organizations · Admin" />
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Organizations</h1>
            <p className="text-muted-foreground text-sm">
              {organizations.length} orgs. Creating one provisions its tenant
              database immediately.
            </p>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>New organization</Button>
            </DialogTrigger>
            <OrgDialog
              org={null}
              parentOptions={parent_options}
              productOptions={product_options}
              onDone={() => setCreateOpen(false)}
            />
          </Dialog>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-medium">
                    {o.name}
                    {o.partner && (
                      <Badge variant="outline" className="ml-2">
                        partner
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {o.slug}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {o.primary_products.map((p) => (
                        <Badge key={p} variant="secondary">
                          {p}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {o.parent?.name ?? "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {o.member_count}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      aria-label={`Edit ${o.name}`}
                      onClick={() => setEditing(o)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Edit dialog — keyed so the form re-initializes per org. */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          {editing && (
            <OrgDialog
              key={editing.id}
              org={editing}
              parentOptions={parent_options}
              productOptions={product_options}
              onDone={() => setEditing(null)}
            />
          )}
        </Dialog>
      </div>
    </AppLayout>
  )
}

function OrgDialog({
  org,
  parentOptions,
  productOptions,
  onDone,
}: {
  org: OrgRow | null
  parentOptions: ParentOption[]
  productOptions: string[]
  onDone: () => void
}) {
  const isEdit = org !== null
  const form = useForm<{
    name: string
    slug: string
    primary_products: string[]
    parent_org_id: string
    partner: boolean
  }>({
    name: org?.name ?? "",
    slug: org?.slug ?? "",
    primary_products: org?.primary_products ?? [],
    parent_org_id: org?.parent ? String(org.parent.id) : NO_PARENT,
    partner: org?.partner ?? false,
  })

  // An org can't be its own parent (the model guards it too, but hide it).
  const parents = isEdit
    ? parentOptions.filter((p) => p.id !== org.id)
    : parentOptions

  function toggleProduct(product: string, checked: boolean) {
    form.setData(
      "primary_products",
      checked
        ? [...form.data.primary_products, product]
        : form.data.primary_products.filter((p) => p !== product),
    )
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    form.transform((data) => {
      const payload = {
        ...data,
        parent_org_id:
          data.parent_org_id === NO_PARENT ? "" : data.parent_org_id,
      }
      // Slug is immutable — never send it on edit.
      if (isEdit) delete (payload as { slug?: string }).slug
      return payload
    })
    const opts = {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        onDone()
      },
    }
    if (isEdit) {
      form.patch(`/admin/organizations/${org.id}`, opts)
    } else {
      form.post("/admin/organizations", opts)
    }
  }

  return (
    <DialogContent>
      <form onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${org.name}` : "New organization"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update products, hierarchy, and partner status. The slug is fixed."
              : "A new tenant with its own database, login, and branding."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
              placeholder="Acme Transload"
              autoFocus
            />
            {form.errors.name && (
              <p className="text-destructive text-sm">{form.errors.name}</p>
            )}
          </div>

          {isEdit ? (
            <div className="space-y-1.5">
              <Label>Slug</Label>
              <p className="text-muted-foreground bg-muted rounded-md px-3 py-2 font-mono text-sm">
                {org.slug}
              </p>
              <p className="text-muted-foreground text-xs">
                Immutable — it's the tenant database filename.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="org-slug">
                Slug{" "}
                <span className="text-muted-foreground font-normal">
                  (optional — defaults from name; immutable once set)
                </span>
              </Label>
              <Input
                id="org-slug"
                value={form.data.slug}
                onChange={(e) => form.setData("slug", e.target.value)}
                placeholder="acme-transload"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Products</Label>
            <div className="grid grid-cols-2 gap-2">
              {productOptions.map((product) => (
                <label
                  key={product}
                  className="flex items-center gap-2 text-sm"
                >
                  <Checkbox
                    checked={form.data.primary_products.includes(product)}
                    onCheckedChange={(c) => toggleProduct(product, c === true)}
                  />
                  {product}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Parent organization (optional)</Label>
            <Select
              value={form.data.parent_org_id}
              onValueChange={(v) => form.setData("parent_org_id", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="None (top-level)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PARENT}>None (top-level)</SelectItem>
                {parents.map((o) => (
                  <SelectItem key={o.id} value={String(o.id)}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center justify-between gap-2">
            <span className="text-sm">
              Partner org
              <span className="text-muted-foreground block text-xs font-normal">
                Can hold child orgs (white-label / customer-with-customers)
              </span>
            </span>
            <Switch
              checked={form.data.partner}
              onCheckedChange={(c) => form.setData("partner", c)}
            />
          </label>
        </div>

        <DialogFooter>
          <Button
            type="submit"
            disabled={form.processing || !form.data.name.trim()}
          >
            {form.processing
              ? "Saving…"
              : isEdit
                ? "Save changes"
                : "Create organization"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
